import config from "@config";
import { getOwnProperty } from "@utils/getOwnProperty";
import {
  Activity,
  Calendar,
  CalendarRange,
  CirclePlus,
  ClipboardCheck,
  FileText,
  GraduationCap,
  HelpCircle,
  LandPlot,
  type LucideIcon,
  Map as MapIcon,
  MessageCircle,
  MessagesSquare,
  Phone,
  PieChart,
  Puzzle,
  Settings,
  Users2,
} from "lucide-react";
import { configToNavChildren } from "./navUtils";

export interface HeaderNavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  /**
   * Registry key for the animated nav glyph. MUST resolve to a Lordicon
   * **System Regular** icon registered in `ui/lordicon.tsx` — that's the house
   * style for main-nav tabs (see DESIGN.md § Navigation). The `icon` (Lucide)
   * is only the load/fallback glyph. Don't add a tab whose key falls through to
   * Lucide; download the matching System Regular JSON and register it first.
   */
  lordiconKey: string;
  /** Small pill rendered next to the label (e.g. "Beta") in More-menu surfaces. */
  badge?: string;
  /** Optional product-maturity grouping for the More menu surfaces. */
  section?: "experimental";
  children?: {
    href: string;
    label: string;
    icon: LucideIcon;
    lordiconKey: string;
  }[];
}

// Main visible navigation items. Children are retained for the mobile menu;
// desktop tabs navigate directly to the item's href.
// NOTE: Children are derived from config.ts (single source of truth)
export const mainNavItems: HeaderNavItem[] = [
  {
    href: "/operator/chat",
    label: "New task",
    icon: CirclePlus,
    lordiconKey: "circle-plus",
  },
  {
    href: "/conversations",
    label: "Conversations",
    icon: MessagesSquare,
    lordiconKey: "messages",
  },
  {
    href: "/campaigns",
    label: "Campaigns",
    icon: LandPlot,
    lordiconKey: "target",
  },
  {
    href: "/coach",
    label: "Coach",
    icon: GraduationCap,
    lordiconKey: "graduation",
  },
  {
    // Day-to-day calling workspace. Keep the mobile submenu while the desktop
    // tab links directly to the dashboard redirect.
    href: "/phone",
    label: "Phones",
    icon: Phone,
    lordiconKey: "phone",
    children: configToNavChildren(config.navigation.subMenus.phone),
  },
];

// Items in the "More" menu
// NOTE: Children are derived from config.ts (single source of truth)
export const moreNavItems: HeaderNavItem[] = [
  {
    // Agent configuration is an administrative destination rather than a
    // day-to-day operating surface, so it leads the stable More menu.
    href: "/agents",
    label: "Agents",
    icon: Users2,
    lordiconKey: "users",
  },
  {
    // Customer-facing since the FSM workspaces gained their own access gates —
    // the /integrations route guard admits super/admin/owner (routeConfig.ts).
    href: "/integrations",
    label: "Integrations",
    icon: Puzzle,
    lordiconKey: "puzzle",
  },
  {
    // Visible to everyone — the /chat-widget teaser handles the disabled state,
    // so the nav carries no role/flag gating (mirrors Booking Wizard).
    href: "/chat-widget",
    label: "Chat Widget",
    // Chat Widget lordicon (see lordicon.tsx "chat-widget"); MessageCircle is
    // the load/fallback glyph.
    icon: MessageCircle,
    lordiconKey: "chat-widget",
  },
  {
    // Visible to everyone — the /booking-wizard teaser (SD-9083) handles the
    // disabled state, so the nav carries no role/flag gating (like Coach).
    href: "/booking-wizard",
    label: "Online booking",
    icon: Calendar,
    lordiconKey: "calendar",
  },
  {
    // Reporting dashboard (SD-9415; merged with the overview). Open to
    // owner/admin/super — the /reports route guard in routeConfig carries the
    // same role set, so the entry never renders a dead/403 link.
    href: "/reports",
    label: "Reports",
    icon: PieChart,
    lordiconKey: "chart",
  },
  {
    // Merged Company + Account workspace (old /company/* and /account/* URLs
    // redirect to /settings/*). Sits last-before-Help: config, like Help, is a
    // leave-the-flow destination, not day-to-day work.
    href: "/settings",
    label: "Settings",
    icon: Settings,
    lordiconKey: "settings",
    children: configToNavChildren(config.navigation.subMenus.settings),
  },
  {
    // Help center (articles + onboarding checklist + support tickets).
    // "help" -> help-center lordicon (see lordicon.tsx); HelpCircle is the
    // load/fallback glyph.
    href: "/help",
    label: "Help",
    icon: HelpCircle,
    lordiconKey: "help",
  },
  {
    href: "/activity",
    label: "Activity",
    icon: Activity,
    lordiconKey: "activity",
    badge: "Beta",
    section: "experimental",
  },

  {
    // Field Sales canvassing module (SD-10989). Reachability (super/admin/owner
    // + rep) is projected from routeConfig via filterReachableNav — no gating
    // here. "map" -> location lordicon (see lordicon.tsx); Map is the
    // load/fallback glyph.
    href: "/field-sales",
    label: "Field Sales",
    icon: MapIcon,
    lordiconKey: "map",
    badge: "Beta",
    section: "experimental",
  },
  {
    // Native scheduling & dispatch board (SCHEDULING-DISPATCH-PLAN.md).
    // "calendar-week" maps to the week-grid calendar Lordicon (ui/lordicon.tsx);
    // CalendarRange is the load/fallback glyph. A week grid, not the plain
    // `calendar` — that one is Booking Wizard's, and two More entries sharing a
    // key render the same animated glyph. The uniqueness guard enforces it.
    href: "/dispatch",
    label: "Dispatch",
    icon: CalendarRange,
    lordiconKey: "calendar-week",
    badge: "Beta",
    section: "experimental",
  },
];

/** Workspace destinations share the app menu's pin controls. */
export const operatorSidebarNavItems: HeaderNavItem[] = [
  {
    href: "/operator/chat/relics",
    label: "Relics",
    icon: FileText,
    lordiconKey: "file",
  },
  {
    href: "/operator/review",
    label: "Review",
    icon: ClipboardCheck,
    lordiconKey: "check",
  },
];

// Super Admin items now live in `superNav.ts` (grouped, role-tagged) — they moved
// out of the More menu into the dedicated Super hub (SD-10904).

export const allMobileNavItems: HeaderNavItem[] = [
  ...mainNavItems,
  ...moreNavItems,
];

/**
 * Nav entries hidden unless the company's expansion flag is on
 * (href -> expansion key). Unlike the teaser-gated entries above (Booking
 * Wizard, Chat Widget), these features are invisible when disabled — the
 * super-only Company Expansion tab is the only way to turn them on.
 */
export const EXPANSION_GATED_NAV = {
  "/dispatch": "dispatch",
  "/dispatch/my-day": "dispatch",
} satisfies Record<string, string>;

/**
 * Drop `section: "experimental"` items (the "Beta"-tagged More-menu group) for
 * the native mobile shell. Apple's guideline 2.2 rejects an App Store build
 * that ships incomplete/beta features users can reach; the web app keeps them.
 * Pure so it can be unit-tested without a Capacitor runtime — the caller passes
 * `Capacitor.isNativePlatform()`.
 */
export function filterExperimentalNavForNative<
  T extends { section?: "experimental" },
>(items: T[], isNativeShell: boolean): T[] {
  if (!isNativeShell) return items;
  return items.filter((item) => item.section !== "experimental");
}

type ExpansionFlags =
  | Record<string, { enabled?: boolean } | undefined>
  | undefined;

/** Drop expansion-gated items whose company flag is off (pure; testable). */
export function filterNavByExpansion<T extends { href: string }>(
  items: T[],
  expansion: ExpansionFlags,
): T[] {
  return items.filter((item) => {
    const flagKey = getOwnProperty(EXPANSION_GATED_NAV, item.href);
    if (!flagKey) return true;
    return expansion?.[flagKey]?.enabled === true;
  });
}
